import React, { useState } from 'react';
import { 
  Container,
  Typography, 
  Box, 
  Button, 
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { invoiceAPI } from '../api/api';
import { CloudUpload as CloudUploadIcon, Receipt as ReceiptIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUploadProgress } from '../contexts/UploadProgressContext';
import { useDashboard } from '../contexts/DashboardContext';

const InvoiceUpload = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [uploadedInvoices, setUploadedInvoices] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');
  const [uploadResults, setUploadResults] = useState([]);
  const navigate = useNavigate();
  const { addUpload, updateUploadProgress, setUploadError, setUploadSuccess, clearCompletedUploads } = useUploadProgress();
  const { refreshDashboard } = useDashboard();

  const onDrop = (acceptedFiles) => {
    // Check if each file type is supported (image or PDF)
    const validFiles = acceptedFiles.filter(file => 
      file.type.startsWith('image/') || file.type === 'application/pdf'
    );
    
    const invalidFiles = acceptedFiles.filter(file => 
      !(file.type.startsWith('image/') || file.type === 'application/pdf')
    );
    
    // Check file size limit (10MB = 10 * 1024 * 1024 bytes)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    const oversizedFiles = validFiles.filter(file => file.size > maxSize);
    
    // Check total file count limit (20 files max)
    const totalFilesAfterAdd = files.length + validFiles.length;
    const maxFiles = 20;
    
    let errorMessage = '';
    
    if (invalidFiles.length > 0) {
      const invalidFileNames = invalidFiles.map(file => file.name).join(', ');
      errorMessage += `Unsupported file type(s): ${invalidFileNames}. Please upload only JPEG, PNG, or PDF files. `;
    }
    
    if (oversizedFiles.length > 0) {
      const oversizedFileNames = oversizedFiles.map(file => `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`).join(', ');
      errorMessage += `File size exceeds 10MB limit: ${oversizedFileNames}. `;
    }
    
    if (totalFilesAfterAdd > maxFiles) {
      errorMessage += `Maximum ${maxFiles} files allowed per upload. `;
    }
    
    if (errorMessage) {
      setError(errorMessage.trim());
      // Even when there's an error (like exceeding file limit), we should still add valid files up to the limit
      // Only add files that pass all validation checks and don't exceed the limit
      let validAndSizeCompliantFiles = validFiles.filter(file => 
        file.size <= maxSize
      );
      
      // Only add files up to the maxFiles limit
      const availableSlots = maxFiles - files.length;
      if (availableSlots > 0) {
        validAndSizeCompliantFiles = validAndSizeCompliantFiles.slice(0, availableSlots);
        if (validAndSizeCompliantFiles.length > 0) {
          setFiles(prevFiles => [...prevFiles, ...validAndSizeCompliantFiles]);
        }
      }
    } else {
      setError('');
      setFiles(prevFiles => [...prevFiles, ...validFiles]);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png'],
      'application/pdf': ['.pdf']
    },
    multiple: true  // Allow multiple files
  });

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one file first');
      return;
    }

    setUploading(true);
    setError('');
    setUploadedInvoices([]);
    setProgress(0);
    setUploadResults([]);

    // Initialize upload results for each file and add to global progress
    const uploadIds = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const uploadId = `upload-${Date.now()}-${i}`;
      uploadIds.push(uploadId);
      
      // Add to global upload progress
      addUpload(uploadId, file.name);
      
      // Initialize local upload result
      setUploadResults(prev => [...prev, {
        id: uploadId,
        fileName: file.name,
        status: 'pending', // pending, uploading, success, error
        progress: 0,
        error: null,
        data: null
      }]);
    }

    // Track progress for multiple files
    const totalFiles = files.length;
    let completedFiles = 0;
    
    try {
      // Process each file individually to track progress
      const results = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const uploadId = uploadIds[i];
        
        // Update the status to uploading for this file
        setUploadResults(prev => prev.map(result => 
          result.id === uploadId ? { ...result, status: 'uploading', progress: 0 } : result
        ));
        
        try {
          // Create a progress handler for this specific file
          const progressHandler = (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              updateUploadProgress(uploadId, percentCompleted);
              setUploadResults(prev => prev.map(result => 
                result.id === uploadId ? { ...result, progress: percentCompleted } : result
              ));
            }
          };
          
          const response = await invoiceAPI.uploadInvoice(file, progressHandler);
          
          // The API call only resolves successfully when the backend has fully processed the file
          // (including data extraction and duplicate check), so we can set success directly
          setUploadSuccess(uploadId);
          setUploadResults(prev => prev.map(result => 
            result.id === uploadId ? { ...result, status: 'success', progress: 100, data: response.data } : result
          ));
          
          results.push(response.data);
          setUploadedInvoices(prev => [...prev, response.data]);
          completedFiles++;
        } catch (err) {
          // Handle different error response formats
          let errorMessage = 'Upload failed';
          if (err.response?.data?.detail) {
            // Handle FastAPI validation errors which can be an array of objects
            if (Array.isArray(err.response.data.detail)) {
              errorMessage = err.response.data.detail.map(error => error.msg || error.detail || JSON.stringify(error)).join(', ');
            } else {
              errorMessage = err.response.data.detail;
            }
          } else if (err.response?.data && Array.isArray(err.response.data)) {
            errorMessage = err.response.data.map(error => error.msg || JSON.stringify(error)).join(', ');
          } else if (typeof err.response?.data === 'object' && err.response.data.msg) {
            errorMessage = err.response.data.msg;
          } else {
            errorMessage = err.response?.data?.detail || 'Upload failed';
          }
          
          // Update status to error for this file
          setUploadError(uploadId, errorMessage);
          setUploadResults(prev => prev.map(result => 
            result.id === uploadId ? { ...result, status: 'error', error: errorMessage } : result
          ));
          
          // Check if it's a duplicate invoice error
          if (err.response?.status === 400 && err.response?.data?.detail?.includes('Duplicate entry for invoice')) {
            setDuplicateMessage(err.response.data.detail);
            setShowDuplicateDialog(true);
          }
          
          completedFiles++;
        }
        
        // Update overall progress
        const currentProgress = Math.min(100, Math.round((completedFiles / totalFiles) * 100));
        setProgress(currentProgress);
      }
      
      // Show confirmation dialog if there were successful uploads
      if (results.length > 0) {
        // Refresh dashboard after successful uploads
        refreshDashboard();
        setShowConfirmation(true);
      } else {
        setError('All uploads failed. Please check the files and try again.');
      }
    } catch (err) {
      // Set progress to 0 on error
      setProgress(0);
      
      setError('An unexpected error occurred during upload.');
      console.error('Upload error:', err);
    } finally {
      // Ensure uploading state is reset
      setUploading(false);
    }
  };

  const removeFile = (indexToRemove) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  const clearAllFiles = () => {
    setFiles([]);
    setError('');
  };

  const handleConfirmation = async (confirmed) => {
    setShowConfirmation(false);
    
    if (confirmed) {
      // If user clicked "Okay", redirect to invoices page
      // Clear completed uploads before navigating
      clearCompletedUploads();
      // Refresh dashboard after successful upload
      refreshDashboard();
      navigate('/invoices');
    } else {
      // If user clicked "Discard", clear the form and reset
      setFiles([]);
      setUploadedInvoices([]);
      setProgress(0);
      setError('');
      setUploadResults([]);
      
      // Clear completed uploads as well
      setTimeout(() => {
        clearCompletedUploads();
      }, 100);
    }
  };

  const handleDuplicateClose = () => {
    setShowDuplicateDialog(false);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2 }}>
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
            <ReceiptIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Upload Invoice
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Upload your invoice to extract data with AI
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card 
            elevation={0} 
            sx={{ 
              backgroundColor: 'background.paper',
              borderRadius: 3,
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                {...getRootProps()}
                sx={{
                  border: `2px dashed ${isDragActive ? 'primary.main' : 'grey.300'}`,
                  borderRadius: 3,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: isDragActive ? 'action.hover' : 'background.default',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderColor: 'primary.main'
                  }
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                {isDragActive ? (
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    Drop the files here ...
                  </Typography>
                ) : (
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    Drag & drop your invoice here
                  </Typography>
                )}
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                  or click to browse files
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supports: JPEG, PNG, PDF files
                </Typography>
              </Box>
              
              {files.length > 0 && (
                <Box mt={3}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Selected Files ({files.length}):
                    </Typography>
                    <Button 
                      onClick={clearAllFiles} 
                      size="small" 
                      color="secondary"
                      sx={{ textTransform: 'none' }}
                    >
                      Clear All
                    </Button>
                  </Box>
                  <Box maxHeight={200} overflow="auto">
                    {files.map((file, index) => {
                      // Find the corresponding upload result for this file
                      const result = uploadResults.find(r => r.fileName === file.name);
                      return (
                        <Box 
                          key={`${file.name}-${index}`} 
                          p={1} 
                          borderRadius={1} 
                          backgroundColor="background.default" 
                          mb={1}
                          display="flex"
                          flexDirection="column"
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="body2" noWrap sx={{ maxWidth: '200px' }}>
                                <strong>{file.name}</strong>
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Size: {(file.size / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                            </Box>
                            <Button 
                              onClick={() => removeFile(index)} 
                              size="small" 
                              color="error"
                            >
                              Remove
                            </Button>
                          </Box>
                          
                          {result && (
                            <Box mt={1}>
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Chip 
                                  label={result.status === 'pending' ? 'Pending' : 
                                         result.status === 'uploading' ? 'Uploading' : 
                                         result.status === 'success' ? 'Success' : 'Error'}
                                  size="small"
                                  color={result.status === 'success' ? 'success' : 
                                         result.status === 'error' ? 'error' : 
                                         result.status === 'uploading' ? 'info' : 'default'}
                                  variant="outlined"
                                />
                                {result.status === 'uploading' && (
                                  <Typography variant="caption" color="text.secondary">
                                    {Math.round(result.progress)}%
                                  </Typography>
                                )}
                              </Box>
                              {result.status === 'uploading' && (
                                <LinearProgress 
                                  variant="determinate" 
                                  value={result.progress} 
                                  sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                                />
                              )}
                              {result.status === 'error' && result.error && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                  {result.error}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
              
              {uploading && (
                <Box mt={3}>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography variant="body2" align="center" mt={1} color="text.secondary">
                    Overall Progress: {Math.round(progress)}%
                  </Typography>
                </Box>
              )}
              
              {error && (
                <Box mt={3}>
                  <Alert severity="error">
                    {typeof error === 'string' 
                      ? error 
                      : Array.isArray(error) 
                        ? error.map((err, idx) => (
                            <div key={idx}>{err.msg || JSON.stringify(err)}</div>
                          ))
                        : error.msg || error.detail || JSON.stringify(error)}
                  </Alert>
                </Box>
              )}
              
              <Box mt={3} display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleUpload}
                  disabled={files.length === 0 || uploading}
                  size="large"
                  sx={{ 
                    px: 4, 
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    backgroundColor: 'primary.main',
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: 'primary.dark'
                    }
                  }}
                >
                  {uploading ? 'Uploading...' : `Upload ${files.length} Invoice${files.length !== 1 ? 's' : ''}`}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0} 
            sx={{ 
              backgroundColor: 'background.paper',
              borderRadius: 3,
              height: '100%'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}>
                How it works
              </Typography>
              
              <Stepper orientation="vertical" sx={{ pl: 1 }}>
                <Step active={true} completed={false}>
                  <StepLabel>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Upload Invoice</Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary">
                      Upload your invoice as an image (JPEG, PNG) or PDF
                    </Typography>
                  </StepContent>
                </Step>
                <Step active={true} completed={false}>
                  <StepLabel>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>AI Processing</Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary">
                      Our AI will extract all relevant data automatically
                    </Typography>
                  </StepContent>
                </Step>
                <Step active={true} completed={false}>
                  <StepLabel>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Review & Save</Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary">
                      Review and save the extracted information to your dashboard
                    </Typography>
                  </StepContent>
                </Step>
              </Stepper>
              
              <Divider sx={{ my: 3 }} />
              
              <Box p={2} borderRadius={2} backgroundColor="info.light" color="info.contrastText">
                <Typography variant="body2" color="info.main">
                  <strong>Tip:</strong> For best results, upload clear, high-resolution images of your invoices.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Confirmation Dialog */}
      <Dialog open={showConfirmation} onClose={() => handleConfirmation(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: 'text.primary' }}>
          {uploadedInvoices.length > 1 
            ? `${uploadedInvoices.length} Invoices Uploaded Successfully` 
            : uploadedInvoices.length === 1 
              ? `Invoice - ${uploadedInvoices[0]?.invoice_number || 'No Invoice Number'} Extracted Successfully` 
              : 'Upload Successful'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {uploadedInvoices.length > 1 
              ? `Successfully processed ${uploadedInvoices.length} invoices.`
              : 'The following data has been extracted from your invoice:'}
          </Typography>
          {uploadedInvoices.length === 1 && (
            <Box p={2} borderRadius={2} backgroundColor="background.paper" color="text.primary" mt={1}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Total Amount:</Typography>
                  <Typography variant="body1" fontWeight="600">${uploadedInvoices[0]?.total_amount}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Currency:</Typography>
                  <Typography variant="body1" fontWeight="600">{uploadedInvoices[0]?.currency}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Invoice Date:</Typography>
                  <Typography variant="body1" fontWeight="600">{uploadedInvoices[0]?.invoice_date}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Payment Status:</Typography>
                  <Typography variant="body1" fontWeight="600">{uploadedInvoices[0]?.payment_status}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Vendor:</Typography>
                  <Typography variant="body1" fontWeight="600">{uploadedInvoices[0]?.vendor?.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Category:</Typography>
                  <Typography variant="body1" fontWeight="600">{uploadedInvoices[0]?.category?.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Invoice Number:</Typography>
                  <Typography variant="body1" fontWeight="600">{uploadedInvoices[0]?.invoice_number || 'Not provided'}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          {uploadedInvoices.length > 1 && (
            <Box p={2} borderRadius={2} backgroundColor="background.paper" color="text.primary" mt={1}>
              <Typography variant="body2" color="text.secondary">
                {uploadedInvoices.length} invoices have been successfully processed and added to your records.
              </Typography>
            </Box>
          )}
          <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
            Would you like to view your invoices?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleConfirmation(false)} color="secondary">
            Stay on Page
          </Button>
          <Button 
            onClick={() => handleConfirmation(true)} 
            variant="contained" 
            color="primary"
            sx={{ 
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
          >
            View Invoices
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Duplicate Invoice Dialog */}
      <Dialog open={showDuplicateDialog} onClose={handleDuplicateClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: 'error.main', display: 'flex', alignItems: 'center' }}>
          <ReceiptIcon sx={{ mr: 1, color: 'error.main' }} />
          Duplicate Invoice Detected
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {duplicateMessage}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please make sure the invoice is not already uploaded in the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDuplicateClose} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InvoiceUpload;